import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';

import { Observable, of } from 'rxjs';
import { filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { ConfigService } from '@fcom/core/services';
import {
  FinnairCabinClass,
  FinnairItineraryItemFlight,
  FinnairItineraryItemType,
  FinnairOrder,
} from '@fcom/dapi/api/models';
import { ResponsiveImage } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { loginStatus, profile } from '@fcom/core/selectors';
import { MediaQueryService } from '@fcom/common/services';
import { AspectRatioString, ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { LoginStatus } from '@fcom/core-api/login';
import { GtmService } from '@fcom/common/gtm';
import { isPresent } from '@fcom/utils';

import { BookingAppState } from '../../../booking/interfaces';

@Component({
  selector: 'fin-promotion-banners',
  templateUrl: './promotion-banners.component.html',
  styleUrls: ['./promotion-banners.component.scss'],
})
export class PromotionBannersComponent implements OnInit {
  readonly LoginStatus = LoginStatus;

  @Input()
  orderData$?: Observable<FinnairOrder> = of(null);

  compensateImage$: Observable<ResponsiveImage>;
  cardImage$: Observable<ResponsiveImage>;
  sustainableTravelImage$: Observable<ResponsiveImage>;
  donatePointsImage$: Observable<ResponsiveImage>;
  showCardBanner$: Observable<boolean>;
  aspectRatio$: Observable<AspectRatioString>;

  loggedIn$: Observable<LoginStatus>;

  chooseUrl$: Observable<string>;
  finnairShopDonateUrl: string;

  cabinClassMap = {
    [FinnairCabinClass.ECONOMY]: 'economy',
    [FinnairCabinClass.ECOPREMIUM]: 'premiumEconomy',
    [FinnairCabinClass.BUSINESS]: 'business',
    [FinnairCabinClass.FIRST]: 'first',
  };

  constructor(
    private configService: ConfigService,
    private languageService: LanguageService,
    private httpClient: HttpClient,
    private store$: Store<BookingAppState>,
    private mediaQueryService: MediaQueryService,
    private gtmService: GtmService
  ) {}

  imageFromFragment = (fragment: string): Observable<ResponsiveImage> =>
    this.languageService.translate(fragment).pipe(
      take(1),
      map((translation) => translation?.picture?.jsonUrl),
      filter(Boolean),
      switchMap((jsonUrl: string) => this.httpClient.get<ResponsiveImage>(jsonUrl))
    );

  ngOnInit(): void {
    this.loggedIn$ = this.store$.pipe(loginStatus());

    this.compensateImage$ = this.imageFromFragment('fragments.compensateChooose');
    this.cardImage$ = this.imageFromFragment('fragments.visaBanner');
    this.sustainableTravelImage$ = this.imageFromFragment('fragments.sustainableTravel');
    this.donatePointsImage$ = this.imageFromFragment('fragments.donatePoints');

    this.aspectRatio$ = this.mediaQueryService
      .isBreakpoint$('mobile')
      .pipe(map((isMobile) => (isMobile ? '1x1' : '16x9')));

    this.showCardBanner$ =
      this.languageService.ddsLocaleValue.countrySite !== 'FI'
        ? of(false)
        : this.store$.pipe(
            profile(),
            map(({ isJuniorMember, hasAktiaVisaCreditCard }) => !isJuniorMember && !hasAktiaVisaCreditCard),
            startWith(true)
          );

    let lang = this.languageService.langKeyValue?.toLowerCase();

    // Only EN FI SV languages are supported by Chooose.
    if (!['en', 'fi', 'sv'].includes(lang)) {
      lang = 'en';
    }

    this.finnairShopDonateUrl = this.configService.cfg.finnairShopDonateUrl.replace('{lang}', lang);

    this.chooseUrl$ = this.orderData$.pipe(
      map((orderData) => {
        if (!isPresent(orderData)) {
          return this.configService.cfg.choooseUrl.replace('{lang}', lang);
        }

        const items = orderData.bounds
          .flatMap((bound) => bound.itinerary)
          .filter((itinerary) => itinerary.type === FinnairItineraryItemType.FLIGHT)
          .reduce((acc: string, itinerary: FinnairItineraryItemFlight, index: number) => {
            const itemParam = `&items[${index}]`;
            return `${acc}
            ${itemParam}[type]=flights.fromTo
            ${itemParam}[from]=${itinerary.departure.locationCode}
            ${itemParam}[to]=${itinerary.arrival.locationCode}
            ${itemParam}[travellers]=${orderData.passengers.length}
            ${itemParam}[travelClass]=${this.cabinClassMap[itinerary.cabinClass]}
            ${itemParam}[roundTrip]=false`;
          }, '')
          .replace(/\s/g, '');
        const htmlElementId = '2d8771d7-f9e7-4ce2-a5d7-caf3276d9b79';

        return `${this.configService.cfg.choooseUrl}?page=cart${items}&orderId=${orderData.id}#scrollTo=${htmlElementId}`.replace(
          '{lang}',
          lang
        );
      })
    );
  }

  trackPromoBanner(banner: string): void {
    this.gtmService.trackElement(
      `${banner}-promotion-banner`,
      GaContext.BOOKING_FLOW,
      ElementTypes.BANNER,
      banner,
      ElementActions.CLICK
    );
  }
}
